import React, { useState, useEffect } from 'react'
import {
  Form,
  message,
  DatePicker,
  Select,
  Table,
  Tag,
  Row,
  Col,
  Card,
  Statistic,
  Progress,
  Tabs,
  Modal,
  Alert,
  Dropdown,
  Button,
  Space,
  Typography,
  Input,
  Tooltip
} from 'antd'
import {
  ReloadOutlined,
  PlusOutlined,
  ArrowLeftOutlined,
  DollarOutlined,
  FileTextOutlined,
  CreditCardOutlined,
  CloseOutlined,
  UserOutlined
} from '@ant-design/icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faBalanceScale, faPercent } from '@fortawesome/free-solid-svg-icons'
import dayjs from 'dayjs'
import { useDispatch } from 'react-redux'
import CoreEnums from '../../../../../../core/utils/enums'
import coreReducer from '../../../../../../core/utils/reducer'
import { InvoiceForm } from '../../../../../Invoices/components'
import RefundModalWrapper from '../../../Refunds/RefundModalWrapper'
import CreditNoteModalWrapper from '../../../CreditNotes/CreditNoteModalWrapper'
import {
  getPatientLedgerTransactions,
  getPatientLedgerSummary,
  recordPayment,
  recordAdjustment,
  recordWriteOff,
  createLedgerTransaction
} from '../../../../backend-integration/api'
import { getRemittances } from '../../../../../Invoices/utils'
import ComprehensiveBalance from '../../../AccountBalance/ComprehensiveBalance'
import TestBillingIntegration from '../../../TestIntegration/TestBillingIntegration'
import Agilite from 'agilite'

const { Title } = Typography

const TransactionsContent = ({ accountData, formatCurrency }) => {
  console.log('TransactionsContent rendering, accountData:', accountData)
  const [showTransactionModal, setShowTransactionModal] = useState(false)
  const [selectedTransactionAction, setSelectedTransactionAction] = useState(null)
  const [showRefundModal, setShowRefundModal] = useState(false)
  const [showCreditNoteModal, setShowCreditNoteModal] = useState(false)
  const [selectedDateRange, setSelectedDateRange] = useState(null)
  const [selectedTransactionType, setSelectedTransactionType] = useState('all')
  const [loading, setLoading] = useState(false)
  const [transactions, setTransactions] = useState([])
  const [allocations, setAllocations] = useState([])
  const [viewMode, setViewMode] = useState('history') // Add missing viewMode state
  const [dataLoaded, setDataLoaded] = useState(false)
  const [summary, setSummary] = useState({
    totalDebits: 0,
    totalCredits: 0,
    currentBalance: 0,
    patientBalance: 0,
    medicalAidBalance: 0
  })
  const [form] = Form.useForm()
  const dispatch = useDispatch()

  // Add missing calculateTotal function
  const calculateTotal = (data, field) => {
    return data.reduce((sum, item) => sum + (item[field] || 0), 0)
  }

  // Load transactions and summary on mount and when dependencies change
  useEffect(() => {
    console.log('TransactionsContent useEffect triggered, accountData:', accountData)
    loadTransactionsAndSummary()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [accountData.billingId, accountData.patientId]) // Use specific deps instead of whole object

  // Add polling for status updates - check every 30 seconds when component is visible
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === 'visible' && dataLoaded) {
        console.log('Auto-refreshing transactions to check for status updates...')
        loadTransactionsAndSummary()
      }
    }, 30000) // Check every 30 seconds

    return () => clearInterval(interval)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataLoaded])

  // Load allocations for this patient
  const loadAllocations = async () => {
    try {
      const remittances = await getRemittances({}, 1, 1000)
      const patientAllocations = []

      remittances.forEach((remittance) => {
        if (remittance.payments && remittance.payments.length > 0) {
          remittance.payments
            .filter((payment) => payment.userRef === accountData.patientId)
            .forEach((payment) => {
              patientAllocations.push({
                ...payment,
                medicalAid: remittance.medicalAid,
                receiptNumber: remittance.receiptNumber,
                remittanceDate: remittance.remittanceDate,
                remittanceFinalized: remittance.finalized,
                key: `${remittance._id}_${payment.invoiceRef}`
              })
            })
        }
      })

      setAllocations(patientAllocations)
    } catch (error) {
      console.error('Failed to load allocations:', error)
    }
  }

  const loadTransactionsAndSummary = async () => {
    // Check for either accountNo or billingId
    if (!accountData.billingId) {
      console.warn('No billingId provided, skipping load')
      return
    }

    setLoading(true)
    try {
      console.log('Starting to load data for:', {
        patientId: accountData.patientId,
        billingId: accountData.billingId,
        fullAccountData: accountData
      })

      // Load transactions - using billingId as primary filter
      // This ensures we get all transactions for this billing account (including dependants)
      const billingId = accountData.billingId

      // Debug logging for Jude Naude
      if (accountData.patientName && accountData.patientName.includes('Jude Naude')) {
        console.log('Account Details - Jude Naude billingId:', {
          billingId: billingId,
          accountData_billingId: accountData.billingId,
          patientId: accountData.patientId
        })
      }

      // Use billingId in the filter - this matches how transactions are stored
      const transactionsData = await getPatientLedgerTransactions({
        billingId: billingId
        // Optional: add patientId to filter for specific patient within billing account
        // patientId: accountData.patientId
      })

      console.log('Transactions loaded:', {
        count: transactionsData?.length || 0,
        firstFew: transactionsData?.slice(0, 3),
        billingIdUsed: billingId
      })

      setTransactions(Array.isArray(transactionsData) ? transactionsData : transactionsData.data || [])

      // Load summary - use billingId as primary identifier
      const summaryData = await getPatientLedgerSummary(billingId, accountData.patientId)
      console.log('AccountDetails - ledger summary:', {
        patientName: accountData.patientName,
        billingId: billingId,
        patientId: accountData.patientId,
        currentBalance: summaryData?.currentBalance,
        medicalAidBalance: summaryData?.medicalAidBalance,
        patientBalance: summaryData?.patientBalance,
        transactionCount: summaryData?.transactionCount
      })
      setSummary(summaryData)

      // Load allocations
      await loadAllocations()

      console.log('Raw transactions data:', transactionsData)
      console.log('Transaction types found:', [...new Set(transactionsData.map((t) => t.type))])
      console.log('First 3 transactions:', transactionsData.slice(0, 3))

      // Enhance invoice transactions with GoodX status
      const enhancedTransactions = await Promise.all(
        transactionsData.map(async (transaction) => {
          // First check if the transaction already has goodXStatus from the ledger
          if (transaction.goodXStatus) {
            console.log('Transaction already has GoodX status:', transaction.goodXStatus)
            return transaction
          }

          // If this is an invoice transaction with an invoiceRef, fetch the invoice to get GoodX status
          if (transaction.type === 'invoice' && transaction.invoiceRef) {
            try {
              const invoiceResponse = await Agilite.Connectors.execute('invoices', 'find-by-id', {
                id: transaction.invoiceRef
              })
              if (invoiceResponse?.data?.goodXStatus) {
                return {
                  ...transaction,
                  goodXStatus: invoiceResponse.data.goodXStatus,
                  goodXTicketNo: invoiceResponse.data.goodXTicketNo
                }
              }
            } catch (err) {
              console.log('Could not fetch invoice for transaction:', transaction.invoiceRef)
            }
          }
          return transaction
        })
      )

      // Calculate running balance
      let runningBalance = 0
      const transactionsWithBalance = enhancedTransactions.map((transaction) => {
        runningBalance += transaction.transactionAmount || 0
        return {
          ...transaction,
          key: transaction._id,
          balance: runningBalance
        }
      })

      console.log('Transactions with balance:', transactionsWithBalance.length)
      console.log('Setting transactions state with:', transactionsWithBalance)
      setTransactions(transactionsWithBalance)
      setDataLoaded(true)
    } catch (error) {
      console.error('Failed to load transactions:', error)
      message.error('Failed to load transaction data')
    } finally {
      setLoading(false)
    }
  }

  const handleRecordTransaction = async (values) => {
    setLoading(true)
    try {
      const billingId = accountData.billingId

      switch (selectedTransactionAction) {
        case 'payment':
          // Validate payment amount against balances before processing
          const paymentAmount = parseFloat(values.amount) || 0
          const patientBalance = summary?.patientBalance || 0
          const medicalAidBalance = summary?.medicalAidBalance || 0

          // Check for overpayment scenarios
          if (paymentAmount > patientBalance && medicalAidBalance > 0) {
            const proceed = await new Promise((resolve) => {
              Modal.confirm({
                title: 'Payment Amount Warning',
                content: (
                  <div>
                    <p>
                      ⚠️ <strong>Potential Overpayment Detected</strong>
                    </p>
                    <p>
                      Payment amount: <strong>R{paymentAmount.toFixed(2)}</strong>
                    </p>
                    <p>
                      Patient balance: <strong>R{patientBalance.toFixed(2)}</strong>
                    </p>
                    <p>
                      Medical aid balance: <strong>R{medicalAidBalance.toFixed(2)}</strong>
                    </p>
                    <br />
                    <p>
                      This payment exceeds the patient balance while medical aid amounts are still outstanding. The
                      system will prevent overpayment during processing.
                    </p>
                  </div>
                ),
                okText: 'Continue',
                cancelText: 'Cancel',
                onOk: () => resolve(true),
                onCancel: () => resolve(false)
              })
            })

            if (!proceed) {
              setLoading(false)
              return
            }
          }

          await recordPayment({
            ...values,
            patientId: accountData.patientId,
            billingId: billingId
          })
          break

        case 'credit':
          // Credit notes are now handled by the dedicated Credit Notes module
          // This case should not be reached anymore
          throw new Error('Credits should be processed through the Credit Notes module')

        case 'discount':
          const discountAmount =
            values.discountType === 'percentage'
              ? (summary.currentBalance * parseFloat(values.amount)) / 100
              : parseFloat(values.amount)

          await createLedgerTransaction({
            type: 'adjustment',
            description: `Discount - ${values.reason}`,
            debit: 0,
            credit: discountAmount,
            patientId: accountData.patientId,
            billingId: billingId,

            notes: values.notes,
            status: 'Processed'
          })
          break

        case 'adjustment':
          await recordAdjustment({
            ...values,
            patientId: accountData.patientId,
            billingId: billingId
          })
          break

        case 'refund':
          // Refunds are now handled by the dedicated Refunds module
          // This case should not be reached anymore
          throw new Error('Refunds should be processed through the Refunds module')

        case 'writeoff':
          await recordWriteOff({
            ...values,
            reason: values.writeoffReason,
            patientId: accountData.patientId,
            billingId: billingId
          })
          break

        case 'handover':
          await createLedgerTransaction({
            type: 'adjustment',
            description: `Handed over to ${values.collectionAgency}`,
            debit: 0,
            credit: 0,
            patientId: accountData.patientId,
            billingId: billingId,

            notes: `Agency: ${values.collectionAgency}, Contact: ${values.contactPerson}, Ref: ${values.agencyReference}`,
            status: 'Handed Over'
          })
          break
      }

      message.success('Transaction recorded successfully')
      form.resetFields()
      setShowTransactionModal(false)
      setSelectedTransactionAction(null)

      // Reload transactions
      loadTransactionsAndSummary()
    } catch (error) {
      console.error('Failed to record transaction:', error)
      message.error('Failed to record transaction')
    } finally {
      setLoading(false)
    }
  }

  const getStatusColor = (type) => {
    const colors = {
      invoice: 'blue',
      payment: 'green',
      medical_aid_payment: 'green',
      medical_aid_approval: 'cyan',
      medical_aid_allocation: 'purple',
      credit: 'cyan',
      adjustment: 'orange',
      refund: 'purple',
      'write off': 'red',
      write_off: 'red',
      handover: 'magenta',
      'balance b/f': 'default'
    }
    return colors[type.toLowerCase()] || colors[type] || 'default'
  }

  const getTransactionTypeLabel = (type, abbreviated = false) => {
    const labels = {
      invoice: abbreviated ? 'INV' : 'Invoice',
      payment: abbreviated ? 'PAY' : 'Payment',
      medical_aid_payment: abbreviated ? 'MAP' : 'Medical Aid Payment',
      medical_aid_approval: abbreviated ? 'MAA' : 'Medical Aid Approved',
      medical_aid_allocation: abbreviated ? 'MAL' : 'Medical Aid Allocated',
      credit: abbreviated ? 'CR' : 'Credit',
      debit: abbreviated ? 'DR' : 'Debit',
      adjustment: abbreviated ? 'ADJ' : 'Adjustment',
      refund: abbreviated ? 'REF' : 'Refund',
      write_off: abbreviated ? 'WO' : 'Write Off',
      credit_note: abbreviated ? 'CN' : 'Credit Note',
      interest: abbreviated ? 'INT' : 'Interest',
      fee: abbreviated ? 'FEE' : 'Fee',
      payment_reversal: abbreviated ? 'PR' : 'Payment Reversal',
      transfer: abbreviated ? 'TRF' : 'Transfer'
    }
    return labels[type] || (abbreviated ? type.toUpperCase().substring(0, 3) : type)
  }

  // Define ledger columns (same as columns)
  const ledgerColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: '15%',
      render: (date) => {
        const formattedDate = dayjs(date).format('DD MMM YYYY')
        return (
          <Tooltip title={`Transaction Date: ${formattedDate}`} placement='topLeft'>
            <div className='billing-table-cell-ellipsis' style={{ fontSize: '12px' }}>
              {formattedDate}
            </div>
          </Tooltip>
        )
      },
      sorter: (a, b) => dayjs(a.date).unix() - dayjs(b.date).unix()
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: '20%',
      render: (type) => {
        const fullLabel = getTransactionTypeLabel(type, false)
        const shortLabel = getTransactionTypeLabel(type, true)
        const color = getStatusColor(type)
        return (
          <div className='billing-table-tags-container'>
            <Tooltip title={`Transaction Type: ${fullLabel}`}>
              <Tag color={color} className='billing-table-tag billing-table-tag-with-tooltip'>
                {shortLabel}
              </Tag>
            </Tooltip>
          </div>
        )
      },
      filters: [
        { text: 'Invoice', value: 'invoice' },
        { text: 'Payment', value: 'payment' },
        { text: 'Medical Aid Payment', value: 'medical_aid_payment' },
        { text: 'Medical Aid Approved', value: 'medical_aid_approval' },
        { text: 'Credit', value: 'credit' },
        { text: 'Debit', value: 'debit' },
        { text: 'Adjustment', value: 'adjustment' },
        { text: 'Write Off', value: 'write_off' }
      ],
      onFilter: (value, record) => record.type === value
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: '25%',
      render: (text, record) => (
        <Tooltip title={text} placement='topLeft'>
          <div className='billing-table-cell-ellipsis'>
            {text}
            {record.invoiceNo && (
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>Invoice: {record.invoiceNo}</div>
            )}
            {record.reference && record.type === 'medical_aid_approval' && (
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>Ref: {record.reference}</div>
            )}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Debit',
      dataIndex: 'debit',
      key: 'debit',
      width: '12%',
      align: 'right',
      render: (amount) =>
        amount > 0 ? (
          <Tooltip title={`Debit: ${formatCurrency(amount)}`} placement='topLeft'>
            <div className='billing-currency' style={{ color: '#cf1322' }}>
              {formatCurrency(amount)}
            </div>
          </Tooltip>
        ) : (
          ''
        ),
      className: 'debit-column'
    },
    {
      title: 'Credit',
      dataIndex: 'credit',
      key: 'credit',
      width: '12%',
      align: 'right',
      render: (amount) =>
        amount > 0 ? (
          <Tooltip title={`Credit: ${formatCurrency(amount)}`} placement='topLeft'>
            <div className='billing-currency' style={{ color: '#52c41a' }}>
              {formatCurrency(amount)}
            </div>
          </Tooltip>
        ) : (
          ''
        ),
      className: 'credit-column'
    },
    {
      title: 'Balance',
      dataIndex: 'balance',
      key: 'balance',
      width: '16%',
      align: 'right',
      render: (amount) => (
        <Tooltip title={`Balance: ${formatCurrency(amount)}`} placement='topLeft'>
          <div
            className='billing-currency'
            style={{
              color: amount > 0 ? '#cf1322' : '#52c41a',
              fontWeight: 'bold'
            }}
          >
            {formatCurrency(amount)}
          </div>
        </Tooltip>
      )
    }
  ]

  // Define history columns (simplified view)
  const historyColumns = [
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      width: 120,
      render: (date) => {
        const formattedDate = dayjs(date).format('DD MMM YYYY')
        return (
          <Tooltip title={`Transaction Date: ${formattedDate}`} placement='topLeft'>
            <div className='billing-table-cell-ellipsis' style={{ fontSize: '12px' }}>
              {formattedDate}
            </div>
          </Tooltip>
        )
      }
    },
    {
      title: 'Type',
      dataIndex: 'type',
      key: 'type',
      width: 150,
      render: (type) => {
        const fullLabel = getTransactionTypeLabel(type, false)
        const shortLabel = getTransactionTypeLabel(type, true)
        return (
          <div className='billing-table-tags-container'>
            <Tooltip title={`Transaction Type: ${fullLabel}`}>
              <Tag color={getStatusColor(type)} className='billing-table-tag billing-table-tag-with-tooltip'>
                {shortLabel}
              </Tag>
            </Tooltip>
          </div>
        )
      }
    },
    {
      title: 'Description',
      dataIndex: 'description',
      key: 'description',
      width: 300,
      render: (text, record) => (
        <Tooltip title={text} placement='topLeft'>
          <div className='billing-table-cell-ellipsis'>
            {text}
            {record.invoiceNo && (
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>Invoice: {record.invoiceNo}</div>
            )}
            {record.reference && (
              <div style={{ fontSize: '11px', color: '#8c8c8c', marginTop: '2px' }}>Ref: {record.reference}</div>
            )}
          </div>
        </Tooltip>
      )
    },
    {
      title: 'Amount',
      key: 'amount',
      width: 150,
      align: 'right',
      render: (_, record) => {
        const amount = record.transactionAmount || record.debit - record.credit
        const displayAmount = amount > 0 ? `+${formatCurrency(amount)}` : formatCurrency(Math.abs(amount))
        return (
          <Tooltip title={`Amount: ${displayAmount}`} placement='topLeft'>
            <div
              className='billing-currency'
              style={{
                color: amount > 0 ? '#cf1322' : '#52c41a',
                fontWeight: 'bold'
              }}
            >
              {displayAmount}
            </div>
          </Tooltip>
        )
      }
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status, record) => {
        // For invoice transactions, show GoodX status if available
        let statusText = record.goodXStatus || status || 'Processed'
        let statusColor = 'default'

        console.log('Rendering status for transaction:', {
          type: record.type,
          status: status,
          goodXStatus: record.goodXStatus,
          statusText: statusText
        })

        // Check if this is an invoice transaction
        if (record.type === 'invoice') {
          // Color code based on GoodX status or regular status
          const goodXStatusColors = {
            mfACK: 'orange', // Medical aid acknowledged (pending)
            mfAPP: 'green', // Medical aid approved
            mfREJ: 'red', // Medical aid rejected
            swREJ: 'red', // Switch rejected
            Processed_successful: 'green',
            PAID: 'green',
            Entry_created: 'blue',
            swACC: 'blue', // Switch accepted
            Private: 'purple',
            Pending: 'orange'
          }
          statusColor = goodXStatusColors[statusText] || 'default'
        } else {
          // For non-invoice transactions
          statusColor = status === 'Pending' ? 'orange' : 'green'
        }

        return (
          <div className='billing-table-tags-container'>
            <Tooltip title={`Status: ${statusText}${record.goodXTicketNo ? ` (Ticket: ${record.goodXTicketNo})` : ''}`}>
              <Tag color={statusColor} className='billing-table-tag billing-table-tag-with-tooltip'>
                {statusText}
              </Tag>
            </Tooltip>
          </div>
        )
      }
    }
  ]

  // Filter transactions based on type and date range
  const getFilteredTransactions = () => {
    let filtered = [...transactions]

    if (selectedTransactionType !== 'all') {
      filtered = filtered.filter((t) => t.type === selectedTransactionType)
    }

    if (selectedDateRange && selectedDateRange.length === 2) {
      filtered = filtered.filter((t) => {
        const transDate = dayjs(t.date)
        return transDate.isAfter(selectedDateRange[0]) && transDate.isBefore(selectedDateRange[1])
      })
    }

    return filtered
  }

  const filteredTransactions = getFilteredTransactions()

  console.log('Current transactions state:', transactions.length, 'items')
  console.log('Filtered transactions:', filteredTransactions.length, 'items')

  // Calculate totals for filtered transactions
  const totalDebit = filteredTransactions.reduce((sum, t) => sum + (t.debit || 0), 0)
  const totalCredit = filteredTransactions.reduce((sum, t) => sum + (t.credit || 0), 0)

  // Use real aging data from the ledger summary
  const agingData = []
  if (summary && summary.agingBuckets) {
    const totalAging = Object.values(summary.agingBuckets).reduce((sum, val) => sum + val, 0)

    agingData.push({
      range: '0-29 days',
      amount: summary.agingBuckets['0-29'] || 0,
      color: '#52c41a',
      percentage: totalAging > 0 ? (((summary.agingBuckets['0-29'] || 0) / totalAging) * 100).toFixed(1) : 0
    })
    agingData.push({
      range: '30-59 days',
      amount: summary.agingBuckets['30-59'] || 0,
      color: '#faad14',
      percentage: totalAging > 0 ? (((summary.agingBuckets['30-59'] || 0) / totalAging) * 100).toFixed(1) : 0
    })
    agingData.push({
      range: '60-89 days',
      amount: summary.agingBuckets['60-89'] || 0,
      color: '#fa8c16',
      percentage: totalAging > 0 ? (((summary.agingBuckets['60-89'] || 0) / totalAging) * 100).toFixed(1) : 0
    })
    agingData.push({
      range: '90+ days',
      amount: summary.agingBuckets['90+'] || 0,
      color: '#f5222d',
      percentage: totalAging > 0 ? (((summary.agingBuckets['90+'] || 0) / totalAging) * 100).toFixed(1) : 0
    })
  }

  return (
    <div>
      <Tabs
        activeKey={viewMode}
        onChange={setViewMode}
        items={[
          {
            key: 'history',
            label: 'Transaction History',
            children: null
          },
          {
            key: 'ledger',
            label: 'Patient Ledger',
            children: null
          },
          {
            key: 'allocations',
            label: 'Medical Aid Allocations',
            children: null
          },
          {
            key: 'balance',
            label: 'Comprehensive Balance',
            children: null
          },
          {
            key: 'test',
            label: 'Test Integration',
            children: null
          }
        ]}
        style={{ marginBottom: 16 }}
      />

      {/* Conditional rendering based on view mode */}
      {viewMode === 'balance' ? (
        <ComprehensiveBalance patientId={accountData.patientId} />
      ) : viewMode === 'test' ? (
        <TestBillingIntegration patientId={accountData.patientId} />
      ) : viewMode === 'allocations' ? (
        // Medical Aid Allocations View
        <Card>
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title='Total Allocated'
                  value={allocations.reduce((sum, alloc) => sum + parseFloat(alloc.paymentAmount || 0), 0)}
                  precision={2}
                  prefix='R'
                  valueStyle={{ color: '#3f8600' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title='Pending Payment'
                  value={allocations.reduce((sum, alloc) => sum + parseFloat(alloc.paymentAmount || 0), 0)}
                  precision={2}
                  prefix='R'
                  valueStyle={{ color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic title='Total Allocations' value={allocations.length} valueStyle={{ color: '#1890ff' }} />
              </Card>
            </Col>
          </Row>

          <Table
            loading={loading}
            dataSource={allocations}
            columns={[
              {
                title: 'Medical Aid',
                dataIndex: 'medicalAid',
                key: 'medicalAid'
              },
              {
                title: 'Receipt #',
                dataIndex: 'receiptNumber',
                key: 'receiptNumber'
              },
              {
                title: 'Allocation Date',
                dataIndex: 'createdAt',
                key: 'createdAt',
                render: (date) => dayjs(date).format('YYYY-MM-DD')
              },
              {
                title: 'Invoice',
                dataIndex: 'invoiceNo',
                key: 'invoiceNo'
              },
              {
                title: 'Allocated Amount',
                dataIndex: 'paymentAmount',
                key: 'paymentAmount',
                align: 'right',
                render: (amount) => formatCurrency(amount)
              },
              {
                title: 'Status',
                key: 'status',
                render: () => <Tag color='orange'>Pending Payment</Tag>
              },
              {
                title: 'Remittance Status',
                dataIndex: 'remittanceFinalized',
                key: 'remittanceFinalized',
                render: (finalized) =>
                  finalized ? <Tag color='green'>Finalized</Tag> : <Tag color='orange'>Draft</Tag>
              }
            ]}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total) => `Total ${total} allocations`
            }}
          />
        </Card>
      ) : (
        <>
          {/* Aging Analysis Cards */}
          <Card size='small' style={{ marginBottom: 32 }}>
            <Row gutter={16}>
              {agingData.map((aging, index) => (
                <Col key={index} xs={24} sm={6}>
                  <div
                    style={{ padding: '12px', borderRadius: '6px', background: '#fafafa', border: '1px solid #f0f0f0' }}
                  >
                    <div style={{ marginBottom: 8 }}>
                      <Typography.Text type='secondary'>{aging.range}</Typography.Text>
                      <br />
                      <Typography.Text strong style={{ color: aging.color, fontSize: '16px' }}>
                        {formatCurrency(aging.amount)}
                      </Typography.Text>
                    </div>
                    <Progress percent={aging.percentage} showInfo={false} strokeColor={aging.color} size='small' />
                  </div>
                </Col>
              ))}
            </Row>
          </Card>

          {/* Filters */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            <Col>
              <DatePicker.RangePicker
                value={selectedDateRange}
                onChange={setSelectedDateRange}
                placeholder={['Start Date', 'End Date']}
                size='small'
              />
            </Col>
            <Col>
              <Select
                value={selectedTransactionType}
                onChange={setSelectedTransactionType}
                style={{ width: 150 }}
                placeholder='Type'
                size='small'
              >
                <Select.Option value='all'>All Types</Select.Option>
                <Select.Option value='invoice'>Invoice</Select.Option>
                <Select.Option value='payment'>Payment</Select.Option>
                <Select.Option value='medical_aid_payment'>Medical Aid Payment</Select.Option>
                <Select.Option value='credit'>Credit</Select.Option>
                <Select.Option value='debit'>Debit</Select.Option>
                <Select.Option value='adjustment'>Adjustment</Select.Option>
                <Select.Option value='refund'>Refund</Select.Option>
                <Select.Option value='write_off'>Write Off</Select.Option>
                <Select.Option value='credit_note'>Credit Note</Select.Option>
              </Select>
            </Col>
            <Col style={{ textAlign: 'right' }}>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadTransactionsAndSummary}
                  loading={loading}
                  title='Refresh transactions'
                >
                  Refresh
                </Button>

                {/* Debug: Create test transactions */}
                <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'test-payment',
                        label: 'Test Payment (R100)',
                        onClick: async () => {
                          try {
                            const billingId = accountData.billingId
                            await recordPayment({
                              amount: 100,
                              paymentMethod: 'cash',
                              reference: 'TEST-PAY-' + Date.now(),
                              patientId: accountData.patientId,
                              billingId: billingId,

                              notes: 'Test payment transaction'
                            })
                            message.success('Test payment created')
                            loadTransactionsAndSummary()
                          } catch (error) {
                            console.error('Failed to create test payment:', error)
                            message.error('Failed to create test payment')
                          }
                        }
                      },
                      {
                        key: 'test-credit',
                        label: 'Test Credit (R50)',
                        onClick: async () => {
                          try {
                            const billingId = accountData.billingId
                            await createLedgerTransaction({
                              type: 'credit',
                              description: 'Test credit adjustment',
                              debit: 0,
                              credit: 50,
                              patientId: accountData.patientId,
                              billingId: billingId,

                              notes: 'Test credit transaction',
                              status: 'Processed'
                            })
                            message.success('Test credit created')
                            loadTransactionsAndSummary()
                          } catch (error) {
                            console.error('Failed to create test credit:', error)
                            message.error('Failed to create test credit')
                          }
                        }
                      },
                      {
                        key: 'test-debit',
                        label: 'Test Debit (R25)',
                        onClick: async () => {
                          try {
                            const billingId = accountData.billingId
                            await createLedgerTransaction({
                              type: 'debit',
                              description: 'Test debit adjustment',
                              debit: 25,
                              credit: 0,
                              patientId: accountData.patientId,
                              billingId: billingId,

                              notes: 'Test debit transaction',
                              status: 'Processed'
                            })
                            message.success('Test debit created')
                            loadTransactionsAndSummary()
                          } catch (error) {
                            console.error('Failed to create test debit:', error)
                            message.error('Failed to create test debit')
                          }
                        }
                      }
                    ]
                  }}
                >
                  <Button icon={<PlusOutlined />}>Add Test Transaction</Button>
                </Dropdown>
                <Button type='primary' icon={<PlusOutlined />} onClick={() => setShowTransactionModal(true)}>
                  Record Transaction
                </Button>
              </Space>
            </Col>
          </Row>

          <Table
            className='billing-module-table'
            columns={viewMode === 'history' ? historyColumns : ledgerColumns}
            dataSource={filteredTransactions}
            pagination={{
              pageSize: 10,
              showSizeChanger: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} transactions`
            }}
            size='small'
            bordered={false}
            scroll={{ x: viewMode === 'history' ? 800 : 1000 }}
            summary={(pageData) => {
              if (viewMode === 'history') {
                // Simplified summary for transaction history
                const pageTotalTransactions = calculateTotal(pageData, 'transactionAmount')
                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Typography.Text strong>Page Total:</Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align='right'>
                      <Typography.Text strong style={{ color: pageTotalTransactions > 0 ? '#cf1322' : '#52c41a' }}>
                        {pageTotalTransactions > 0
                          ? `+${formatCurrency(pageTotalTransactions)}`
                          : formatCurrency(pageTotalTransactions)}
                      </Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} />
                  </Table.Summary.Row>
                )
              } else {
                // Detailed summary for patient ledger
                const pageTotalDebit = calculateTotal(pageData, 'debit')
                const pageTotalCredit = calculateTotal(pageData, 'credit')
                const pageNetBalance = pageTotalDebit - pageTotalCredit

                return (
                  <Table.Summary.Row>
                    <Table.Summary.Cell index={0} colSpan={3}>
                      <Typography.Text strong>Page Total:</Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={1} align='right'>
                      <Typography.Text strong>{formatCurrency(pageTotalDebit)}</Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={2} align='right'>
                      <Typography.Text strong>{formatCurrency(pageTotalCredit)}</Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={3} align='right'>
                      <Typography.Text strong style={{ color: pageNetBalance > 0 ? '#cf1322' : '#52c41a' }}>
                        {formatCurrency(pageNetBalance)}
                      </Typography.Text>
                    </Table.Summary.Cell>
                    <Table.Summary.Cell index={4} />
                  </Table.Summary.Row>
                )
              }
            }}
          />

          {/* Record Transaction Modal */}
          <Modal
            title='Record Transaction'
            visible={showTransactionModal}
            onCancel={() => {
              setShowTransactionModal(false)
              setSelectedTransactionAction(null)
              form.resetFields()
            }}
            footer={null}
            width={600}
          >
            {!selectedTransactionAction ? (
              <div>
                <Title level={5} style={{ marginBottom: 16 }}>
                  Select Transaction Type
                </Title>
                <Row gutter={[16, 16]}>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => setSelectedTransactionAction('payment')}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <DollarOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Record Payment</span>
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => {
                        // Close the modal
                        setShowTransactionModal(false)
                        setSelectedTransactionAction(null)
                        form.resetFields()

                        // Open InvoiceForm in a new tab, same as billing dashboard
                        const tabContent = {
                          key: `${CoreEnums.tabKeys.INVOICES}_new`,
                          closable: true,
                          label: 'New Invoice',
                          children: <InvoiceForm data={{}} isNew={true} handleGetInvoices={() => {}} />
                        }
                        dispatch(coreReducer.actions.addTab(tabContent))
                      }}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FileTextOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Raise Invoice</span>
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => {
                        // Close the transaction modal and open credit note modal
                        setShowTransactionModal(false)
                        setSelectedTransactionAction(null)
                        form.resetFields()
                        setShowCreditNoteModal(true)
                      }}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CreditCardOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Create Credit Note</span>
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => setSelectedTransactionAction('discount')}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faPercent} style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Apply Discount</span>
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => setSelectedTransactionAction('adjustment')}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <FontAwesomeIcon icon={faBalanceScale} style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Pass Adjustment</span>
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => {
                        // Close the transaction modal and open refund modal
                        setShowTransactionModal(false)
                        setSelectedTransactionAction(null)
                        form.resetFields()
                        setShowRefundModal(true)
                      }}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <DollarOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Process Refund</span>
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => setSelectedTransactionAction('writeoff')}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <CloseOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Write Off Debt</span>
                    </Button>
                  </Col>
                  <Col span={12}>
                    <Button
                      block
                      size='large'
                      onClick={() => setSelectedTransactionAction('handover')}
                      style={{
                        height: 80,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center'
                      }}
                    >
                      <UserOutlined style={{ fontSize: 24, marginBottom: 8 }} />
                      <span>Hand Over</span>
                    </Button>
                  </Col>
                </Row>
              </div>
            ) : (
              <div>
                <Button
                  type='text'
                  icon={<ArrowLeftOutlined />}
                  onClick={() => setSelectedTransactionAction(null)}
                  style={{ marginBottom: 16 }}
                >
                  Back to Transaction Types
                </Button>

                {selectedTransactionAction === 'payment' && (
                  <Form form={form} layout='vertical' onFinish={handleRecordTransaction}>
                    <Title level={5}>Record Payment</Title>
                    <Form.Item
                      name='paymentDate'
                      label='Payment Date'
                      rules={[{ required: true, message: 'Please select payment date' }]}
                      initialValue={dayjs()}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name='amount'
                      label='Payment Amount'
                      rules={[{ required: true, message: 'Please enter amount' }]}
                    >
                      <Input type='number' prefix='R' placeholder='0.00' min={0} step='0.01' />
                    </Form.Item>
                    <Form.Item
                      name='paymentMethod'
                      label='Payment Method'
                      rules={[{ required: true, message: 'Please select payment method' }]}
                    >
                      <Select placeholder='Select payment method'>
                        <Select.Option value='cash'>Cash</Select.Option>
                        <Select.Option value='eft'>EFT</Select.Option>
                        <Select.Option value='card'>Card</Select.Option>
                        <Select.Option value='cheque'>Cheque</Select.Option>
                        <Select.Option value='medical_aid'>Medical Aid</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name='reference'
                      label='Reference Number'
                      rules={[
                        ({ getFieldValue }) => ({
                          validator(_, value) {
                            const paymentMethod = getFieldValue('paymentMethod')
                            if (paymentMethod === 'eft' && (!value || value.trim().length === 0)) {
                              return Promise.reject(new Error('Reference number is required for EFT payments'))
                            }
                            return Promise.resolve()
                          }
                        })
                      ]}
                    >
                      <Input placeholder='Payment reference (required for EFT)' />
                    </Form.Item>
                    <Form.Item name='notes' label='Notes'>
                      <Input.TextArea rows={3} placeholder='Additional notes...' />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button type='primary' htmlType='submit'>
                          Record Payment
                        </Button>
                        <Button
                          onClick={() => {
                            setShowTransactionModal(false)
                            setSelectedTransactionAction(null)
                            form.resetFields()
                          }}
                        >
                          Cancel
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                )}

                {/* Credit forms are now handled by the dedicated Credit Notes module */}

                {selectedTransactionAction === 'discount' && (
                  <Form form={form} layout='vertical' onFinish={handleRecordTransaction}>
                    <Title level={5}>Apply Discount</Title>
                    <Form.Item
                      name='discountDate'
                      label='Discount Date'
                      rules={[{ required: true, message: 'Please select discount date' }]}
                      initialValue={dayjs()}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name='discountType'
                      label='Discount Type'
                      rules={[{ required: true, message: 'Please select discount type' }]}
                    >
                      <Select placeholder='Select discount type'>
                        <Select.Option value='percentage'>Percentage</Select.Option>
                        <Select.Option value='amount'>Fixed Amount</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name='amount'
                      label='Discount Value'
                      rules={[{ required: true, message: 'Please enter discount value' }]}
                    >
                      <Input
                        type='number'
                        placeholder='0.00'
                        min={0}
                        step='0.01'
                        addonBefore={form.getFieldValue('discountType') === 'percentage' ? '%' : 'R'}
                      />
                    </Form.Item>
                    <Form.Item
                      name='reason'
                      label='Reason for Discount'
                      rules={[{ required: true, message: 'Please enter reason' }]}
                    >
                      <Input placeholder='Loyalty discount, early payment, etc.' />
                    </Form.Item>
                    <Form.Item name='notes' label='Notes'>
                      <Input.TextArea rows={3} placeholder='Additional notes...' />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button type='primary' htmlType='submit'>
                          Apply Discount
                        </Button>
                        <Button
                          onClick={() => {
                            setShowTransactionModal(false)
                            setSelectedTransactionAction(null)
                            form.resetFields()
                          }}
                        >
                          Cancel
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                )}

                {selectedTransactionAction === 'adjustment' && (
                  <Form form={form} layout='vertical' onFinish={handleRecordTransaction}>
                    <Title level={5}>Pass Adjustment</Title>
                    <Form.Item
                      name='adjustmentDate'
                      label='Adjustment Date'
                      rules={[{ required: true, message: 'Please select adjustment date' }]}
                      initialValue={dayjs()}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name='adjustmentType'
                      label='Adjustment Type'
                      rules={[{ required: true, message: 'Please select adjustment type' }]}
                    >
                      <Select placeholder='Select adjustment type'>
                        <Select.Option value='debit'>Debit (Increase Balance)</Select.Option>
                        <Select.Option value='credit'>Credit (Decrease Balance)</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name='amount'
                      label='Adjustment Amount'
                      rules={[{ required: true, message: 'Please enter amount' }]}
                    >
                      <Input type='number' prefix='R' placeholder='0.00' min={0} step='0.01' />
                    </Form.Item>
                    <Form.Item
                      name='reason'
                      label='Reason for Adjustment'
                      rules={[{ required: true, message: 'Please enter reason' }]}
                    >
                      <Input placeholder='Correction, write-off, etc.' />
                    </Form.Item>
                    <Form.Item name='notes' label='Notes'>
                      <Input.TextArea rows={3} placeholder='Additional notes...' />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button type='primary' htmlType='submit'>
                          Pass Adjustment
                        </Button>
                        <Button
                          onClick={() => {
                            setShowTransactionModal(false)
                            setSelectedTransactionAction(null)
                            form.resetFields()
                          }}
                        >
                          Cancel
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                )}

                {/* Refund forms are now handled by the dedicated Refunds module */}

                {selectedTransactionAction === 'writeoff' && (
                  <Form form={form} layout='vertical' onFinish={handleRecordTransaction}>
                    <Title level={5}>Write Off Debt</Title>
                    <Alert
                      message='Warning: This action will permanently write off the debt'
                      type='warning'
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item
                      name='writeoffDate'
                      label='Write Off Date'
                      rules={[{ required: true, message: 'Please select write off date' }]}
                      initialValue={dayjs()}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name='amount'
                      label='Write Off Amount'
                      rules={[{ required: true, message: 'Please enter write off amount' }]}
                    >
                      <Input type='number' prefix='R' placeholder='0.00' min={0} step='0.01' />
                    </Form.Item>
                    <Form.Item
                      name='writeoffReason'
                      label='Write Off Reason'
                      rules={[{ required: true, message: 'Please select write off reason' }]}
                    >
                      <Select placeholder='Select write off reason'>
                        <Select.Option value='bad_debt'>Bad Debt</Select.Option>
                        <Select.Option value='charity'>Charity/Goodwill</Select.Option>
                        <Select.Option value='deceased'>Patient Deceased</Select.Option>
                        <Select.Option value='dispute'>Dispute Resolution</Select.Option>
                        <Select.Option value='uncollectable'>Uncollectable</Select.Option>
                        <Select.Option value='other'>Other</Select.Option>
                      </Select>
                    </Form.Item>
                    <Form.Item
                      name='authorization'
                      label='Authorized By'
                      rules={[{ required: true, message: 'Please enter who authorized this write off' }]}
                    >
                      <Input placeholder='Manager name or authorization code' />
                    </Form.Item>
                    <Form.Item name='notes' label='Notes'>
                      <Input.TextArea rows={3} placeholder='Additional notes...' />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button type='primary' danger htmlType='submit'>
                          Write Off Debt
                        </Button>
                        <Button
                          onClick={() => {
                            setShowTransactionModal(false)
                            setSelectedTransactionAction(null)
                            form.resetFields()
                          }}
                        >
                          Cancel
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                )}

                {selectedTransactionAction === 'handover' && (
                  <Form form={form} layout='vertical' onFinish={handleRecordTransaction}>
                    <Title level={5}>Hand Over Account</Title>
                    <Alert
                      message='This will mark the account as handed over to debt collection'
                      type='warning'
                      showIcon
                      style={{ marginBottom: 16 }}
                    />
                    <Form.Item
                      name='handoverDate'
                      label='Handover Date'
                      rules={[{ required: true, message: 'Please select handover date' }]}
                      initialValue={dayjs()}
                    >
                      <DatePicker style={{ width: '100%' }} />
                    </Form.Item>
                    <Form.Item
                      name='collectionAgency'
                      label='Collection Agency'
                      rules={[{ required: true, message: 'Please enter collection agency name' }]}
                    >
                      <Input placeholder='Agency name' />
                    </Form.Item>
                    <Form.Item
                      name='amount'
                      label='Amount Handed Over'
                      rules={[{ required: true, message: 'Please enter amount' }]}
                    >
                      <Input type='number' prefix='R' placeholder='0.00' min={0} step='0.01' />
                    </Form.Item>
                    <Form.Item
                      name='agencyReference'
                      label='Agency Reference Number'
                      rules={[{ required: true, message: 'Please enter agency reference' }]}
                    >
                      <Input placeholder='Reference number from collection agency' />
                    </Form.Item>
                    <Form.Item name='contactPerson' label='Contact Person'>
                      <Input placeholder='Contact person at collection agency' />
                    </Form.Item>
                    <Form.Item name='notes' label='Notes'>
                      <Input.TextArea rows={3} placeholder='Additional notes...' />
                    </Form.Item>
                    <Form.Item>
                      <Space>
                        <Button type='primary' danger htmlType='submit'>
                          Hand Over Account
                        </Button>
                        <Button
                          onClick={() => {
                            setShowTransactionModal(false)
                            setSelectedTransactionAction(null)
                            form.resetFields()
                          }}
                        >
                          Cancel
                        </Button>
                      </Space>
                    </Form.Item>
                  </Form>
                )}
              </div>
            )}
          </Modal>

          {/* Refund Modal */}
          <RefundModalWrapper
            visible={showRefundModal}
            onClose={() => setShowRefundModal(false)}
            preSelectedPatient={{
              _id: accountData.patientId,
              name: accountData.patientName,
              billingId: accountData.billingId,
              fileNo: accountData.fileNo
            }}
            onSuccess={loadTransactionsAndSummary}
          />

          {/* Credit Note Modal */}
          <CreditNoteModalWrapper
            visible={showCreditNoteModal}
            onClose={() => setShowCreditNoteModal(false)}
            preSelectedPatient={{
              _id: accountData.patientId,
              name: accountData.patientName,
              billingId: accountData.billingId,
              fileNo: accountData.fileNo
            }}
            onSuccess={loadTransactionsAndSummary}
          />
        </>
      )}
    </div>
  )
}

export default TransactionsContent
